import type { McpEvent } from "./Mcp";
import type { CompactionProgressEvent, CompactionEvent } from "./Compaction";
import type { TokenUsageEvent } from "./TokenUsageEvent";
import type { TaskStatusValue } from "./TaskRegistry";
import type { PendingInjectionKind } from "./longTask";

interface BaseMessage {
  /** Uniquely identifies this message. */
  uuid: string;
  /** Markdown content. */
  content: string;
  /** Hidden from the UI when true. */
  hide?: boolean;
}

/** High-level conversation instruction; always hidden from the UI. */
export interface SystemMessage extends BaseMessage {
  role: "system";
  hide: true;
}

export interface UserMessage extends BaseMessage {
  role: "user";
}

/**
 * A hidden user-role message injected by the frontend to drive the agent
 * during long-task workflows (ADR 059).
 *
 * `longTaskJobId` and `kind` are what `hasCommittedTerminalDirective` matches on,
 * so the message list is itself the record — needing no server round-trip — that a
 * job's terminal directive was already announced before a reload.
 *
 * `status`/`statusReason` are a durable record of how the job ended, and nothing
 * reads them back: the authoritative terminal status lives on the task row.
 */
export interface SyntheticUserMessage extends UserMessage {
  directive: true;
  hide: true;
  longTaskJobId?: string;
  kind?: PendingInjectionKind;
  status?: TaskStatusValue;
  statusReason?: string;
}

/** A stored form of a file, e.g. `original` or `pdf_preview` (ADR 055). */
export interface FileVariant {
  variant_id: string;
  variant_type: string;
}

export interface FileMessageMeta {
  file_id?: string;
  filename?: string;
  variants?: FileVariant[];
}

/** File attachment. Messages without `meta` are legacy format (no file_id). */
export interface FileMessage extends BaseMessage {
  role: "file";
  meta?: FileMessageMeta;
}

/** Regular assistant response (no tool call). */
export interface AssistantResponseMessage extends BaseMessage {
  role: "assistant";
  /** Progress/logging events from MCP tool calls. */
  mcpEvents?: McpEvent[];
  tokenUsage?: TokenUsageEvent["data"];
  /** General server error from the SSE stream. */
  errorMessage?: string | null;
  /** True when compaction was attempted but failed or was skipped this turn. */
  compactionFailed?: boolean;
}

/**
 * Tool call initiated by the assistant.
 * Uses role "assistant" to match the OpenAI convention (the assistant is requesting the call).
 * CC API example: {"type":"tool_call","role":"assistant","id":"toolu_vrtx_01...","tool_name":"get_forecast","tool_input":{...}}
 */
export interface ToolCallMessage extends BaseMessage {
  role: "assistant";
  /** Unique ID of this tool call, e.g. "toolu_vrtx_01MtYxTKQRBaWuKcZoMnx3Nz". */
  id: string;
  tool_name: string;
  /** Null when the tool is not from an MCP server. */
  mcp_server: string | null;
  tool_input: Record<string, unknown>;
  /** Frontend-only: denormalized content of the corresponding tool result message. */
  toolResult?: string;
  /** Frontend-only: true when the tool call failed. */
  isError?: boolean;
  /** Frontend-only: async task ID from the tool result, if any. */
  task_id?: string;
  /** Frontend-only: recommended poll interval (ms) for the async task. */
  task_poll_interval?: number;
  /** Frontend-only: human-readable name of the async task. */
  task_name?: string;
}

/**
 * Tool result from the CC API.
 * Example: {"type":"tool_result","role":"tool","tool_name":"get_forecast","content":"...","tool_call_id":"toolu_vrtx_01..."}
 */
export interface ToolResultMessage extends BaseMessage {
  role: "tool";
  /** ID of the corresponding tool call. */
  tool_call_id: string;
  tool_name: string;
  /** True when the tool call failed; content will contain the error. */
  isError?: boolean;
  /** Async task ID, when the result corresponds to a task. */
  task_id?: string;
  /** Recommended poll interval (ms) for the async task. */
  task_poll_interval?: number;
  /** Human-readable name of the async task. */
  task_name?: string;
}

/** Frontend-only composite combining an assistant's tool calls, results, and response text. */
export interface AssistantToolsMessage extends BaseMessage {
  role: "assistant";
  subMessages: Message[];
}

/**
 * Summary produced by backend compaction. Inserted after the last message it
 * covers; everything before it is no longer sent to the model.
 */
export type CompactionMessage = BaseMessage & CompactionEvent;

/** Transient progress indicator shown while compaction is running. */
export type CompactionProgressMessage = BaseMessage & {
  role: "compaction_progress";
} & CompactionProgressEvent;

export type Message =
  | SystemMessage
  | UserMessage
  | SyntheticUserMessage
  | FileMessage
  | AssistantResponseMessage
  | ToolCallMessage
  | ToolResultMessage
  | AssistantToolsMessage
  | CompactionMessage
  | CompactionProgressMessage;

export function isSyntheticUserMessage(
  msg: Message
): msg is SyntheticUserMessage {
  return (
    msg.role === "user" && (msg as SyntheticUserMessage).directive === true
  );
}

export function isSystemMessage(msg: Message): msg is SystemMessage {
  return msg.role === "system";
}

export function isUserMessage(msg: Message): msg is UserMessage {
  return msg.role === "user";
}

export function isFileMessage(msg: Message): msg is FileMessage {
  return msg.role === "file";
}

/**
 * Identifies file messages that pre-date the direct file-api upload path
 * (ADR 002). Legacy messages have no `meta.file_id`.
 */
export function isLegacyFileMessage(msg: FileMessage): boolean {
  return !msg.meta?.file_id;
}

export function isAssistantResponseMessage(
  msg: Message
): msg is AssistantResponseMessage {
  return msg.role === "assistant" && !("id" in msg) && !("subMessages" in msg);
}

export function isToolCallMessage(msg: Message): msg is ToolCallMessage {
  return msg.role === "assistant" && "id" in msg;
}

/**
 * Reads a string field from a tool call's `tool_input`. Returns undefined when
 * the call is missing or the field is absent/non-string, so callers don't have
 * to cast `tool_input` (typed `Record<string, unknown>`) at each access site.
 */
export function readToolInputString(
  toolCall: ToolCallMessage | undefined,
  field: string
): string | undefined {
  const value = toolCall?.tool_input?.[field];
  return typeof value === "string" ? value : undefined;
}

export function isToolResultMessage(msg: Message): msg is ToolResultMessage {
  return msg.role === "tool";
}

export function isAssistantToolsMessage(
  msg: Message
): msg is AssistantToolsMessage {
  return msg.role === "assistant" && "subMessages" in msg;
}

export function isCompactionMessage(msg: Message): msg is CompactionMessage {
  return msg.role === "compaction";
}

export function isCompactionProgressMessage(
  msg: Message
): msg is CompactionProgressMessage {
  return msg.role === "compaction_progress";
}

export type MessageRole = Message["role"];

/**
 * Strips frontend-only fields before sending a message to the API, keeping only
 * an allowlist of fields the OpenAI-compatible chain accepts. `uuid` is on the
 * allowlist (the chain tolerates it and it aids correlation); `directive`,
 * `longTaskJobId`, `kind`, etc. are frontend-only and stripped.
 */
const ALLOWED_API_FIELDS = new Set([
  "role",
  "content",
  "tool_call_id",
  "id",
  "tool_name",
  "tool_input",
  "meta",
  "uuid",
  "covers_through_message_uuid",
  "covered_message_count",
  "summary_tokens",
  "model",
]);

export function formatMessageForApi(message: Message) {
  let formattedMessage = { ...message };

  for (const key of Object.keys(message)) {
    if (!ALLOWED_API_FIELDS.has(key)) {
      delete formattedMessage[key as keyof Message];
    }
  }

  return formattedMessage;
}
